import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { FormBuilder, usePrevious } from '@as/cotopaxi';
import { DefaultSettings, FetchStatus, RefundInformationMessageType, ReturnedSaleLineStatusTypes, ReturnedSaleStatusTypes, } from '@as-react/common-types';
import { Banner, Box, Breakpoint, Divider, LinkButton, Stack, TextAlpha } from '@yonderland/andes-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { RETURN_ITEM_REDIRECT_PREFIX } from '../../constants/AccountConstant';
import { RETURN_ITEMS_COMPONENT } from '../../constants/DOMElementsConstants';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { useOAuth } from '../../hooks/useOAuth';
import { analyticsActions } from '../../redux/analytics/actions/AnalyticsActions.creators';
import { ordersActions } from '../../redux/orders/actions/OrdersActions.creators';
import { makeGetAnnouncedReturn, makeGetReturnItems, makeGetReturnReasons, makeGetSubmitReturnItemsState, } from '../../redux/orders/selectors/OrdersSelectors';
import { makeGetLocationHash } from '../../redux/router/selectors/RouterSelector';
import { ScrollUtil } from '../../util/ScrollUtil';
import { HtmlInclude } from '../common/html-include/HtmlInclude';
import { VerticalStep, VerticalStepStatus } from './../vertical-step/VerticalStep';
import { NewAnnouncedReturn } from './NewAnnouncedReturn';
import { ReturnItemsChangeHandler } from './ReturnItemsChangeHandler';
import { ReturnItemsEmptyState } from './ReturnItemsEmptyState';
import { ReturnItemsList } from './ReturnItemsList';
import { ReturnItemsMethodForm } from './ReturnItemsMethodForm';
import { UsedReturnOption } from './UsedReturnOption';
export var ReturnItemsSteps;
(function (ReturnItemsSteps) {
    ReturnItemsSteps[ReturnItemsSteps["ITEMS"] = 0] = "ITEMS";
    ReturnItemsSteps[ReturnItemsSteps["METHOD"] = 1] = "METHOD";
})(ReturnItemsSteps || (ReturnItemsSteps = {}));
const validationSchemasReturnItems = (t) => Yup.object({
    saleLines: Yup.array().of(Yup.object().shape({
        returnReasonId: Yup.string().when('isSelected', {
            is: true,
            then: schema => schema.required(t('account:returnItems.form.reasonForReturn.error')),
        }),
        returnReasonFreeText: Yup.string().max(DefaultSettings.MAX_LENGTH_TEXT_AREA, ' '),
    })),
});
const validationSchemasMethods = Yup.object({
    countryId: Yup.string().required(),
    returnOptionId: Yup.string().required(),
});
export const ReturnItems = ({ configuredReturnOptions }) => {
    const { t } = useTranslation(I18N_NAMESPACES.account);
    const { isLoggedIn } = useOAuth();
    const dispatch = useDispatch();
    const returnItems = useSelector(makeGetReturnItems());
    const returnReasons = useSelector(makeGetReturnReasons());
    const [activeStep, setActiveStep] = useState(ReturnItemsSteps.ITEMS);
    const locationHashValue = useSelector(makeGetLocationHash());
    const submitState = useSelector(makeGetSubmitReturnItemsState());
    const announcedReturn = useSelector(makeGetAnnouncedReturn());
    const prevSubmitState = usePrevious(submitState);
    const [success, setSuccess] = useState(false);
    const hasSentPageLoadRule = useRef(false);
    useEffect(() => {
        if (!isLoggedIn) {
            return;
        }
        dispatch(ordersActions.getReturnItems());
        dispatch(ordersActions.getReturnReasons());
    }, [isLoggedIn]);
    useEffect(() => {
        if (locationHashValue.includes(RETURN_ITEM_REDIRECT_PREFIX) && returnItems && (returnItems === null || returnItems === void 0 ? void 0 : returnItems.length) > 0) {
            ScrollUtil.scrollToElement(document.querySelector(`.${locationHashValue.substr(1)}`));
        }
        if (returnItems !== null && !hasSentPageLoadRule.current) {
            dispatch(analyticsActions.returnPortalReturnOverviewOpened((returnItems === null || returnItems === void 0 ? void 0 : returnItems.length) > 0));
            hasSentPageLoadRule.current = true;
        }
    }, [returnItems]);
    useEffect(() => {
        if (submitState === FetchStatus.RESOLVED && prevSubmitState === FetchStatus.PENDING) {
            setSuccess(true);
            ScrollUtil.scrollToElement(document.querySelector(RETURN_ITEMS_COMPONENT));
        }
    }, [submitState, prevSubmitState]);
    useEffect(() => {
        if (activeStep === ReturnItemsSteps.METHOD) {
            dispatch(analyticsActions.returnMethodOverviewOpened());
        }
    }, [activeStep]);
    const returnItemsValidationSchema = {
        [ReturnItemsSteps.ITEMS]: validationSchemasReturnItems(t),
        [ReturnItemsSteps.METHOD]: validationSchemasReturnItems(t).concat(validationSchemasMethods),
    };
    const initialFormValues = React.useMemo(() => {
        if (!returnItems) {
            return null;
        }
        const saleLines = returnItems.reduce((acc, returnItem) => {
            const returnItemSalesLines = returnItem.saleLines.map(saleLine => ({
                saleId: returnItem.saleId,
                saleLineId: saleLine.saleLineId,
                isSelected: false,
                qty: 1,
                originalPaymentMethod: returnItem.originalPaymentMethod,
                returnReasonId: undefined,
                customer: returnItem.customerResource,
            }));
            return [...acc, ...returnItemSalesLines];
        }, []);
        return {
            returnOptionId: '',
            saleLines,
        };
    }, [returnItems]);
    const getStepStatus = (step) => {
        if (step < activeStep) {
            return VerticalStepStatus.PASSED;
        }
        if (step === activeStep) {
            return VerticalStepStatus.CURRENT;
        }
        return undefined;
    };
    const handleStepClick = (step) => {
        if (step >= activeStep) {
            return undefined;
        }
        return () => {
            setActiveStep(step);
        };
    };
    const isMultipleSales = (array) => {
        var _a, _b;
        if (array && array.length > 1) {
            const loopLength = array.length;
            const firstValue = (_a = array === null || array === void 0 ? void 0 : array[0]) === null || _a === void 0 ? void 0 : _a.saleId;
            for (let i = 1; i < loopLength; i++) {
                if (((_b = array === null || array === void 0 ? void 0 : array[i]) === null || _b === void 0 ? void 0 : _b.saleId) !== firstValue) {
                    return true;
                }
            }
        }
        return false;
    };
    if (!returnItems) {
        return null;
    }
    if (returnItems.length === 0) {
        return _jsx(ReturnItemsEmptyState, { message: t('returnItems.emptyState.message') });
    }
    if (success && !!announcedReturn) {
        const lines = announcedReturn.announcedReturns.reduce((acc, announcedReturnObj) => {
            const findSale = returnItems.find(val => val.saleId === announcedReturnObj.saleId);
            if (findSale) {
                const findSaleLine = findSale.saleLines.find(val => val.saleLineId === announcedReturnObj.saleLineId);
                if (findSaleLine) {
                    const returnReason = returnReasons.find(reason => reason.returnReasonId === announcedReturnObj.returnReasonId);
                    return [
                        ...acc,
                        Object.assign(Object.assign({}, findSaleLine), { quantity: announcedReturnObj.qty, typeId: ReturnedSaleLineStatusTypes.ANNOUNCED, returnReasonDescription: returnReason === null || returnReason === void 0 ? void 0 : returnReason.description }),
                    ];
                }
            }
            return acc;
        }, []);
        const newAnnouncedReturns = {
            lines: lines,
            returnFromMultipleSales: isMultipleSales(announcedReturn.announcedReturns),
            statusMessage: ReturnedSaleStatusTypes.ANNOUNCED,
        };
        const statusMessage = (_jsx(Banner, { text: _jsxs(_Fragment, { children: [_jsx(TextAlpha, { variant: "inherit", strong: true, as: "span", children: t('returnItems.success.status.label') }), t('returnItems.success.status.toBeReceived')] }), status: "informative" }));
        const usedReturnOption = configuredReturnOptions
            ? Object.assign(Object.assign({}, (configuredReturnOptions.find(item => item.courier === announcedReturn.carrierName) ||
                {})), { costPrice: announcedReturn.shippingCost, refundInformationMessageType: announcedReturn.originalPaymentMethod
                    ? announcedReturn.originalPaymentMethod
                    : RefundInformationMessageType.OTHER, hasMultipleInvoices: newAnnouncedReturns.returnFromMultipleSales }) : null;
        return (_jsxs(_Fragment, { children: [_jsx(TextAlpha, { as: "h2", variant: "subheadingL", children: t('returnItems.success.heading') }), _jsx(Box, { paddingBlock: "4", children: _jsx(TextAlpha, { children: (usedReturnOption === null || usedReturnOption === void 0 ? void 0 : usedReturnOption.successMessage) ? (_jsx(HtmlInclude, { htmlFragment: usedReturnOption.successMessage })) : (t('returnItems.success.content')) }) }), _jsx(Box, { paddingBlock: "4", marginInline: { base: '-4', md: '-6', lg: '0' }, children: statusMessage }), _jsx(Box, { paddingBlockStart: "4", paddingBlockEnd: "8", children: _jsxs(TextAlpha, { as: "h2", variant: "subheadingM", children: [t('returnItems.confirmation.summary.heading'), " ", announcedReturn.announcementReference] }) }), !!newAnnouncedReturns && _jsx(NewAnnouncedReturn, { returnedSale: newAnnouncedReturns }), !!usedReturnOption && (_jsx(UsedReturnOption, Object.assign({}, usedReturnOption, { announcementReference: announcedReturn.announcementReference })))] }));
    }
    const handleSubmit = (values) => {
        if (activeStep === ReturnItemsSteps.ITEMS) {
            setActiveStep(ReturnItemsSteps.METHOD);
            dispatch(analyticsActions.returnProductsContinueClicked());
            ScrollUtil.scrollToElement(document.querySelector(RETURN_ITEMS_COMPONENT));
        }
        else if (activeStep === ReturnItemsSteps.METHOD) {
            const saleLineIds = values.saleLines
                .filter(saleLine => saleLine.isSelected)
                .map(saleLine => ({
                qty: saleLine.qty,
                saleId: saleLine.saleId,
                saleLineId: saleLine.saleLineId,
                returnReasonFreeText: saleLine.returnReasonFreeText,
                returnReasonId: saleLine.returnReasonId,
                originalPaymentMethod: saleLine.originalPaymentMethod,
            }));
            dispatch(analyticsActions.returnMethodConfirmClickClicked(values.returnOptionId || ''));
            dispatch(ordersActions.submitReturnItems({
                saleLineIds,
                address: values.address,
                returnCountryId: values.countryId,
                returnOptionId: values.returnOptionId ? parseInt(values.returnOptionId, 10) : 0,
            }));
        }
    };
    return (initialFormValues && (_jsx(FormBuilder, { onSubmit: handleSubmit, validationSchema: returnItemsValidationSchema[activeStep], initialValues: initialFormValues, validateOnBlur: true, validateOnChange: false, name: "form-return-items", children: ({ values }) => {
            var _a;
            const selectedSaleLines = (_a = values === null || values === void 0 ? void 0 : values.saleLines) === null || _a === void 0 ? void 0 : _a.filter(saleLine => saleLine.isSelected);
            const returnHasMultipleSales = isMultipleSales(selectedSaleLines);
            return (_jsxs(_Fragment, { children: [_jsx(Box, { marginInline: { base: '-4', md: '0' }, children: _jsx(Divider, {}) }), _jsxs(VerticalStep, { index: ReturnItemsSteps.ITEMS, title: _jsxs(Stack, { justify: "between", align: "center", wrap: "nowrap", gap: "2", flex: { grow: 1 }, children: [_jsx("div", { onClick: handleStepClick(ReturnItemsSteps.ITEMS), role: "button", tabIndex: 0, children: _jsx(TextAlpha, { as: "h2", variant: "subheadingS", children: t('returnItems.step1.heading') }) }), getStepStatus(ReturnItemsSteps.ITEMS) === VerticalStepStatus.PASSED && (_jsx(Breakpoint, { from: "sm", children: _jsxs(Stack, { gap: "1", align: "center", children: [_jsx(TextAlpha, { variant: "bodyS", children: t('returnItems.tab.returnItems.selectedCount', {
                                                    count: selectedSaleLines.length,
                                                }) }), _jsx(LinkButton, { onClick: handleStepClick(ReturnItemsSteps.ITEMS), size: "sm", children: t('returnItems.tab.returnItems.selectedCount.action') })] }) }))] }), status: getStepStatus(ReturnItemsSteps.ITEMS), children: [_jsx(Divider, {}), _jsx(Box, { paddingBlock: "6", children: _jsx(ReturnItemsList, { returnItems: returnItems, returnReasons: returnReasons }) })] }), _jsx(Box, { marginInline: { base: '-4', md: '0' }, children: _jsx(Divider, {}) }), _jsxs(VerticalStep, { index: ReturnItemsSteps.METHOD, title: _jsx(TextAlpha, { as: "h2", variant: "subheadingS", children: t('returnItems.step2.heading') }), status: getStepStatus(ReturnItemsSteps.METHOD), children: [_jsx(Divider, {}), _jsx(Box, { paddingBlockStart: "6", children: _jsx(ReturnItemsMethodForm, { configuredReturnOptions: configuredReturnOptions, returnHasMultipleSales: returnHasMultipleSales, saleLines: selectedSaleLines }) })] }), getStepStatus(ReturnItemsSteps.METHOD) !== VerticalStepStatus.CURRENT && (_jsx(Box, { marginInline: { base: '-4', md: '0' }, children: _jsx(Divider, {}) })), _jsx(ReturnItemsChangeHandler, {})] }));
        } })));
};
